import { useEffect, useMemo, useRef, useState } from 'react';
import { ManageLayout } from '../../components/ManageLayout';
import css from './Days.module.css';

interface Day {
  id: string;
  label: string;
  exercise_ids_json: string;
  rotation_index: number;
  on_demand: number;
  hidden: number;
  session_count: number;
}

interface ExerciseLite {
  id: string;
  name: string;
  region: string | null;
  hidden: number;
}

export function ManageDays() {
  const [days, setDays] = useState<Day[] | null>(null);
  const [exercises, setExercises] = useState<ExerciseLite[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const [dRes, eRes] = await Promise.all([
        fetch('/api/manage/days'),
        fetch('/api/manage/exercises'),
      ]);
      const d = (await dRes.json()) as { days: Day[] };
      const e = (await eRes.json()) as {
        exercises: { id: string; name: string; region: string | null; hidden: number }[];
      };
      setDays(d.days);
      setExercises(
        e.exercises.map((x) => ({
          id: x.id,
          name: x.name,
          region: x.region,
          hidden: x.hidden,
        })),
      );
    } catch {
      setError("couldn't load days");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const rotation = useMemo(() => (days ?? []).filter((d) => !d.on_demand), [days]);
  const onDemand = useMemo(() => (days ?? []).filter((d) => d.on_demand), [days]);

  async function commitReorder(newOrder: Day[]) {
    setError(null);
    // Optimistic update
    const order = newOrder.map((d, i) => ({ id: d.id, rotation_index: i }));
    setDays((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      for (const o of order) {
        const idx = next.findIndex((d) => d.id === o.id);
        if (idx >= 0) next[idx] = { ...next[idx]!, rotation_index: o.rotation_index };
      }
      return next.sort((a, b) => {
        if (a.on_demand !== b.on_demand) return a.on_demand - b.on_demand;
        return a.rotation_index - b.rotation_index;
      });
    });
    try {
      const res = await fetch('/api/manage/days/reorder', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ order }),
      });
      if (!res.ok) throw new Error(`reorder failed: ${res.status}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'reorder failed');
      void reload();
    }
  }

  function handleDrop(targetId: string) {
    if (!draggingId || draggingId === targetId) return;
    const src = rotation.findIndex((d) => d.id === draggingId);
    const tgt = rotation.findIndex((d) => d.id === targetId);
    if (src < 0 || tgt < 0) return;
    const next = [...rotation];
    const [moved] = next.splice(src, 1);
    next.splice(tgt, 0, moved!);
    void commitReorder(next);
    setDraggingId(null);
    setDragOverId(null);
  }

  return (
    <ManageLayout title="Days">
      {error && <div className={css.error}>{error}</div>}
      <div className={css.toolbar}>
        <span style={{ color: '#888', fontSize: 12 }}>
          Drag rotation cards to reorder. On-demand days are triggered by label, not by rotation.
        </span>
        <button type="button" className={css.btn} onClick={() => setCreateOpen(true)}>
          new day
        </button>
      </div>

      <section className={css.section}>
        <h2 className={css.sectionTitle}>Rotation ({rotation.length})</h2>
        {!days ? (
          <div className={css.empty}>loading…</div>
        ) : rotation.length === 0 ? (
          <div className={css.empty}>no rotation days</div>
        ) : (
          <ul className={css.dayList}>
            {rotation.map((d) => (
              <DayCard
                key={d.id}
                day={d}
                exercises={exercises ?? []}
                open={openId === d.id}
                draggable
                isDragOver={dragOverId === d.id}
                onToggle={() => setOpenId(openId === d.id ? null : d.id)}
                onSaved={reload}
                onDragStart={() => setDraggingId(d.id)}
                onDragOver={() => setDragOverId(d.id)}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDragOverId(null);
                }}
                onDrop={() => handleDrop(d.id)}
              />
            ))}
          </ul>
        )}
      </section>

      <section className={css.section}>
        <h2 className={css.sectionTitle}>On-demand ({onDemand.length})</h2>
        {onDemand.length === 0 ? (
          <div className={css.empty}>no on-demand days yet — use "new day" above</div>
        ) : (
          <ul className={css.dayList}>
            {onDemand.map((d) => (
              <DayCard
                key={d.id}
                day={d}
                exercises={exercises ?? []}
                open={openId === d.id}
                draggable={false}
                isDragOver={false}
                onToggle={() => setOpenId(openId === d.id ? null : d.id)}
                onSaved={reload}
                onDragStart={() => {}}
                onDragOver={() => {}}
                onDragEnd={() => {}}
                onDrop={() => {}}
              />
            ))}
          </ul>
        )}
      </section>

      {createOpen && (
        <CreateDayModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void reload();
          }}
        />
      )}
    </ManageLayout>
  );
}

// --------------------------------------------------------------------------

function DayCard({
  day,
  exercises,
  open,
  draggable,
  isDragOver,
  onToggle,
  onSaved,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  day: Day;
  exercises: ExerciseLite[];
  open: boolean;
  draggable: boolean;
  isDragOver: boolean;
  onToggle: () => void;
  onSaved: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}) {
  return (
    <li
      className={`${css.dayCard}${day.hidden ? ` ${css.dayCardHidden}` : ''}${isDragOver ? ` ${css.dragOver}` : ''}`}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragOver={(e) => {
        if (!draggable) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDragLeave={() => onDragOver()}
      onDragEnd={onDragEnd}
      onDrop={(e) => {
        if (!draggable) return;
        e.preventDefault();
        onDrop();
      }}
    >
      <div className={css.dayHeader} onClick={onToggle} role="button" tabIndex={0}>
        <span className={css.dragHandle} aria-hidden="true">
          {draggable ? '⋮⋮' : '·'}
        </span>
        <div>
          <span className={css.label}>{day.label}</span>
          <span className={css.labelMeta}>{day.id}</span>
          {day.hidden ? <span className={css.labelMeta}>(hidden)</span> : null}
        </div>
        <div className={css.col}>{parseJsonArray(day.exercise_ids_json).length} ex</div>
        <div className={css.col}>{day.session_count} session{day.session_count === 1 ? '' : 's'}</div>
        <div className={css.caret}>{open ? '▴' : '▾'}</div>
      </div>
      {open && <DayDetail day={day} exercises={exercises} onSaved={onSaved} />}
    </li>
  );
}

function DayDetail({
  day,
  exercises,
  onSaved,
}: {
  day: Day;
  exercises: ExerciseLite[];
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(day.label);
  const [onDemand, setOnDemand] = useState(day.on_demand === 1);
  const [exerciseIds, setExerciseIds] = useState<string[]>(() =>
    parseJsonArray(day.exercise_ids_json),
  );
  const [addPick, setAddPick] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const savedTimer = useRef<number | null>(null);

  const canHide = day.session_count === 0 && !day.hidden;

  const isDirty = useMemo(() => {
    if (label.trim() !== day.label) return true;
    if (onDemand !== (day.on_demand === 1)) return true;
    const before = parseJsonArray(day.exercise_ids_json);
    if (exerciseIds.length !== before.length) return true;
    for (let i = 0; i < exerciseIds.length; i++) {
      if (exerciseIds[i] !== before[i]) return true;
    }
    return false;
  }, [label, onDemand, exerciseIds, day]);

  useEffect(() => {
    return () => {
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
    };
  }, []);

  const visibleExercises = useMemo(
    () => exercises.filter((e) => !e.hidden).sort((a, b) => a.name.localeCompare(b.name)),
    [exercises],
  );

  // Group by region for the picker. Empty/null region → "(unsorted)" bucket.
  const groupedExercises = useMemo(() => {
    const groups = new Map<string, ExerciseLite[]>();
    for (const ex of visibleExercises) {
      const key = ex.region?.trim() || '(unsorted)';
      let list = groups.get(key);
      if (!list) {
        list = [];
        groups.set(key, list);
      }
      list.push(ex);
    }
    return [...groups.entries()].sort(([a], [b]) => {
      // Push "(unsorted)" to the bottom; otherwise alphabetical.
      if (a === '(unsorted)') return 1;
      if (b === '(unsorted)') return -1;
      return a.localeCompare(b);
    });
  }, [visibleExercises]);

  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= exerciseIds.length) return;
    const next = [...exerciseIds];
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setExerciseIds(next);
  }
  function remove(idx: number) {
    setExerciseIds((prev) => prev.filter((_, i) => i !== idx));
  }
  function add() {
    if (!addPick) return;
    if (exerciseIds.includes(addPick)) return;
    setExerciseIds((prev) => [...prev, addPick]);
    setAddPick('');
  }

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`/api/manage/days/${day.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          label,
          on_demand: onDemand,
          exerciseIds,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error ?? `save failed: ${res.status}`);
      }
      setSavedAt(Date.now());
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSavedAt(null), 2000);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  async function hide() {
    if (!canHide) return;
    setError(null);
    try {
      const res = await fetch(`/api/manage/days/${day.id}/hide`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error ?? `hide failed: ${res.status}`);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'hide failed');
    }
  }

  async function unhide() {
    setError(null);
    try {
      const res = await fetch(`/api/manage/days/${day.id}/unhide`, { method: 'POST' });
      if (!res.ok) throw new Error(`unhide failed: ${res.status}`);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unhide failed');
    }
  }

  return (
    <div className={css.body}>
      {error && <div className={css.error}>{error}</div>}

      <div className={css.field}>
        <label className={css.fieldLabel}>label</label>
        <input
          className={css.input}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>

      <div className={css.fieldRow}>
        <label className={css.fieldLabel}>
          <input
            type="checkbox"
            checked={onDemand}
            onChange={(e) => setOnDemand(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          on-demand (triggered by label, not in rotation)
        </label>
      </div>

      <div>
        <label className={css.fieldLabel}>exercises (in order)</label>
        <ul className={css.exerciseList}>
          {exerciseIds.map((eid, idx) => {
            const ex = exercises.find((x) => x.id === eid);
            return (
              <li key={`${eid}-${idx}`} className={css.exerciseRow}>
                <span className={css.dragHandle}>⋮⋮</span>
                <span className={ex ? css.exerciseName : css.exerciseMissing}>
                  {ex ? ex.name : `${eid} (missing)`}
                </span>
                <button
                  type="button"
                  className={css.smallBtn}
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  aria-label="move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={`${css.smallBtn} ${css.smallBtnDanger}`}
                  onClick={() => remove(idx)}
                  aria-label="remove"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
        <div className={css.addRow}>
          <select value={addPick} onChange={(e) => setAddPick(e.target.value)}>
            <option value="">+ add exercise…</option>
            {groupedExercises.map(([region, items]) => (
              <optgroup key={region} label={region}>
                {items.map((e) => (
                  <option key={e.id} value={e.id} disabled={exerciseIds.includes(e.id)}>
                    {e.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button type="button" className={css.btnGhost} onClick={add} disabled={!addPick}>
            add
          </button>
        </div>
      </div>

      <div className={css.actions}>
        <span className={css.refsHint}>
          {day.session_count === 0
            ? 'no past sessions — can be hidden'
            : `${day.session_count} past session${day.session_count === 1 ? '' : 's'} reference this day — cannot hide`}
        </span>
        {day.hidden ? (
          <button type="button" className={css.btnGhost} onClick={() => void unhide()}>
            unhide
          </button>
        ) : (
          <button
            type="button"
            className={css.btnDanger}
            onClick={() => void hide()}
            disabled={!canHide}
          >
            delete (hide)
          </button>
        )}
        <button
          type="button"
          className={css.btn}
          onClick={() => void save()}
          disabled={saving || !isDirty}
          title={!isDirty && !saving ? 'no changes to save' : undefined}
        >
          {saving ? 'saving…' : savedAt ? 'saved ✓' : isDirty ? 'save' : 'saved'}
        </button>
      </div>
    </div>
  );
}

function CreateDayModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState('');
  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [onDemand, setOnDemand] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function nameToId(s: string): string {
    return s
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/manage/days', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          id: id || undefined,
          label,
          exerciseIds: [],
          on_demand: onDemand,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? `create failed: ${res.status}`);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'create failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={css.modal} onClick={onClose}>
      <div className={css.modalCard} onClick={(e) => e.stopPropagation()}>
        <h2 className={css.modalTitle}>New day</h2>
        {error && <div className={css.error}>{error}</div>}
        <div className={css.field}>
          <label className={css.fieldLabel}>label</label>
          <input
            className={css.input}
            value={label}
            placeholder="e.g. Shoulders"
            onChange={(e) => {
              setLabel(e.target.value);
              if (!idTouched) setId(nameToId(e.target.value));
            }}
          />
        </div>
        <div className={css.field}>
          <label className={css.fieldLabel}>id (slug)</label>
          <input
            className={css.input}
            value={id}
            onChange={(e) => {
              setId(e.target.value);
              setIdTouched(true);
            }}
          />
        </div>
        <div className={css.fieldRow}>
          <label className={css.fieldLabel}>
            <input
              type="checkbox"
              checked={onDemand}
              onChange={(e) => setOnDemand(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            on-demand (not in rotation)
          </label>
        </div>
        <p style={{ color: '#888', fontSize: 12, margin: 0 }}>
          Add exercises after creating — easier to pick from the full list inside the day card.
        </p>
        <div className={css.actions}>
          <button type="button" className={css.btnGhost} onClick={onClose}>
            cancel
          </button>
          <button
            type="button"
            className={css.btn}
            onClick={() => void submit()}
            disabled={submitting || !label.trim()}
          >
            {submitting ? 'creating…' : 'create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseJsonArray(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
